import React, { useState, useEffect, useRef } from 'react';
import { User, Video, Image, Trash2, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { uploadImage } from '../../lib/storage';
import { supabase } from '../../lib/supabase';
import { VideoIntroModal } from './VideoIntroModal';
import { AvatarPhotoLightbox } from './AvatarPhotoLightbox';
import { BottomSheet } from '../ui/BottomSheet';
import { useTranslation } from 'react-i18next';

interface AvatarProps {
  url?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  userId?: string;
  username?: string;
  onUpdate?: () => void;
  editable?: boolean;
  disableLink?: boolean;
  videoIntroUrl?: string;
  isOwnProfile?: boolean;
  showVideoIndicator?: boolean;
}

// Branding colors
const brandingGreen = '#2F4F3E';
const brandingTerracotta = '#C75B39';
const videoRingGreen = '#8DA847';

export function Avatar({
  url,
  size = 'md',
  userId,
  username,
  onUpdate,
  editable = false,
  disableLink = false,
  videoIntroUrl: initialVideoUrl,
  isOwnProfile = false,
  showVideoIndicator = false
}: AvatarProps) {
  const { t } = useTranslation();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(url);
  const [loading, setLoading] = useState(false);
  const [videoIntroUrl, setVideoIntroUrl] = useState<string | undefined>(initialVideoUrl);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
  const [videoModalMode, setVideoModalMode] = useState<'upload' | 'playback'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = `avatar-upload-${userId || 'default'}-${Math.random().toString(36).slice(2, 9)}`;

  // Fetch avatar and video intro data
  useEffect(() => {
    // Always set avatar URL first - this works regardless of showVideoIndicator
    setAvatarUrl(url);
    setVideoIntroUrl(initialVideoUrl);

    // Only fetch video data if we're on profile page (showVideoIndicator is true)
    // and we have a userId and url wasn't provided
    if (!showVideoIndicator || !userId) {
      return;
    }

    if (!loading && !url) {
      setLoading(true);
      supabase
        .from('users')
        .select('avatar_url, video_intro_url')
        .eq('id', userId)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            if (data.avatar_url) {
              setAvatarUrl(data.avatar_url);
            }
            if (data.video_intro_url) {
              setVideoIntroUrl(data.video_intro_url);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching user data:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [url, userId, initialVideoUrl, showVideoIndicator]);

  const sizeClasses = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-32 w-32',
    xl: 'h-20 w-20',
    '2xl': 'h-24 w-24'
  };

  const iconSize = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-8 h-8',
    xl: 'w-6 h-6',
    '2xl': 'w-6 h-6'
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const publicUrl = await uploadImage(file, 'avatars');
      if (!publicUrl) return;

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update user profile
      await supabase
        .from('users')
        .update({
          avatar_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      // Update local state
      setAvatarUrl(publicUrl);

      // Call onUpdate callback if provided
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating avatar:', error);
    }
  };

  const handleAvatarClick = (e: React.MouseEvent) => {
    if (!showVideoIndicator) return;

    if (editable || isOwnProfile) {
      e.preventDefault();
      e.stopPropagation();
      setShowOptionsModal(true);
      return;
    }

    // Other user's profile: tap photo to enlarge (intro video opens only via play button)
    if (avatarUrl) {
      e.preventDefault();
      e.stopPropagation();
      setShowPhotoLightbox(true);
    }
  };

  const handlePlayIntroClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!videoIntroUrl) return;
    setVideoModalMode('playback');
    setShowVideoModal(true);
  };

  const handleChooseProfilePicture = () => {
    setShowOptionsModal(false);
    // Small delay to allow modal to close before triggering file input
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 50);
  };

  const handleChooseVideoIntro = () => {
    setShowOptionsModal(false);
    setVideoModalMode('upload');
    setShowVideoModal(true);
  };

  const handleWatchVideoIntro = () => {
    setShowOptionsModal(false);
    setVideoModalMode('playback');
    setShowVideoModal(true);
  };

  const handleDeleteVideoIntro = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('users')
        .update({
          video_intro_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      setVideoIntroUrl(undefined);
      setShowOptionsModal(false);

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting video intro:', error);
    }
  };

  const handleVideoUploaded = async (url: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('users')
        .update({
          video_intro_url: url || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      setVideoIntroUrl(url);

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving video URL:', error);
    }
  };

  const hasVideoIntro = !!videoIntroUrl && showVideoIndicator;

  const showProfileUploadUI = editable || isOwnProfile;
  const showOptionsSheet = showVideoIndicator && (editable || isOwnProfile);
  /** No upload/sheet/modals — avoids rendering a hidden file input that parent layouts can accidentally unhide (e.g. `[&>*]:block`). */
  const isDisplayOnlyAvatar = !editable && !showVideoIndicator && !isOwnProfile;
  const avatarCursorClass =
    editable || showVideoIndicator ? 'cursor-pointer' : 'cursor-default';

  // Calculate size for play button based on avatar size
  const playButtonSize = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-8 h-8',
    xl: 'w-6 h-6',
    '2xl': 'w-8 h-8'
  };

  // Avatar with optional glossy green ring and play icon
  const renderAvatar = () => (
    <>
      {/* Glossy green gradient ring - outside overflow-hidden so it's visible */}
      {hasVideoIntro && (
        <div
          className="absolute rounded-full"
          style={{
            width: `calc(100% + 16px)`,
            height: `calc(100% + 16px)`,
            top: '-8px',
            left: '-8px',
            background: `linear-gradient(135deg, #A3C94A 0%, #8DA847 25%, #7A9A3D 50%, #8DA847 75%, #A3C94A 100%)`,
            zIndex: 0,
            boxShadow: '0 4px 15px rgba(141, 168, 71, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
        />
      )}

      {/* Main avatar container */}
      <div
        className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gray-100 relative group ${avatarCursorClass}`}
        style={{ zIndex: 1 }}
        onClick={handleAvatarClick}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="Profile" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gray-100">
            <User className="h-1/2 w-1/2 text-gray-400" />
          </div>
        )}

        {/* Play control: separate hit target so photo tap = lightbox, play = intro video */}
        {hasVideoIntro && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <button
              type="button"
              onClick={handlePlayIntroClick}
              className={`pointer-events-auto ${playButtonSize[size]} rounded-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent`}
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.22) 100%)',
                backdropFilter: 'blur(2px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.35)'
              }}
              aria-label={t('profile.watchIntro')}
            >
              <Play className={`${size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} text-white fill-white ml-0.5`} />
            </button>
          </div>
        )}

        {/* Hover overlay for own profile - Change */}
        {editable && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
            <span className="text-white text-xs">Change</span>
          </div>
        )}

      </div>
    </>
  );

  const avatarWrapper = (
    <div className="relative inline-block">
      {renderAvatar()}
    </div>
  );

  if (isDisplayOnlyAvatar) {
    return avatarWrapper;
  }

  return (
    <>
      {avatarWrapper}

      {showProfileUploadUI && (
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          className="hidden"
          accept="image/*"
          onChange={handleAvatarUpload}
        />
      )}

      {showOptionsSheet && (
        <BottomSheet
          isOpen={showOptionsModal}
          onClose={() => setShowOptionsModal(false)}
          title={
            <span className="font-gelica text-lg" style={{ color: brandingGreen }}>
              {t('profile.profilePicture')}
            </span>
          }
          desktopMode="modal"
          desktopMaxWidthClass="max-w-sm"
          panelClassName="bg-gradient-to-b from-[#F3F7EE] via-white to-white border-t border-accent-text/10 lg:border-0"
          contentClassName="px-0"
        >
          <div className="px-2 pb-4">
            <button
              type="button"
              onClick={handleChooseProfilePicture}
              className="w-full flex items-center gap-4 p-4 hover:bg-white/80 rounded-xl transition-colors text-left"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${brandingTerracotta}20` }}
              >
                <Image className="w-5 h-5" style={{ color: brandingTerracotta }} />
              </div>
              <div>
                <p className="font-semibold text-content">{t('profile.changeProfilePicture')}</p>
                <p className="text-sm text-content/55 font-figtree">{t('profile.uploadNewPhoto')}</p>
              </div>
            </button>

            {hasVideoIntro ? (
              <>
                <button
                  type="button"
                  onClick={handleWatchVideoIntro}
                  className="w-full flex items-center gap-4 p-4 hover:bg-white/80 rounded-xl transition-colors text-left"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-accent-text/15"
                  >
                    <Play className="w-5 h-5 text-accent-text" />
                  </div>
                  <div>
                    <p className="font-semibold text-content">{t('profile.watchYourIntro')}</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleChooseVideoIntro}
                  className="w-full flex items-center gap-4 p-4 hover:bg-white/80 rounded-xl transition-colors text-left"
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-accent-text/15">
                    <Video className="w-5 h-5 text-accent-text" />
                  </div>
                  <div>
                    <p className="font-semibold text-content">{t('profile.changeVideoIntro')}</p>
                    <p className="text-sm text-content/55 font-figtree">{t('profile.updateYourIntro')}</p>
                  </div>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleChooseVideoIntro}
                className="w-full flex items-center gap-4 p-4 hover:bg-white/80 rounded-xl transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-accent-text/15">
                  <Video className="w-5 h-5 text-accent-text" />
                </div>
                <div>
                  <p className="font-semibold text-content">{t('profile.addVideoIntro')}</p>
                  <p className="text-sm text-content/55 font-figtree">{t('profile.record15SecVideo')}</p>
                </div>
              </button>
            )}

            {hasVideoIntro && (
              <button
                type="button"
                onClick={handleDeleteVideoIntro}
                className="w-full flex items-center gap-4 p-4 hover:bg-red-50 rounded-xl transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-red-50">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-content">{t('profile.removeVideoIntro')}</p>
                  <p className="text-sm text-content/55 font-figtree">{t('profile.deleteYourIntro')}</p>
                </div>
              </button>
            )}
          </div>
        </BottomSheet>
      )}

      {showVideoIndicator && (
        <VideoIntroModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
          mode={videoModalMode}
          videoUrl={videoIntroUrl}
          onVideoUploaded={handleVideoUploaded}
        />
      )}

      {showVideoIndicator && (
        <AvatarPhotoLightbox
          isOpen={showPhotoLightbox}
          onClose={() => setShowPhotoLightbox(false)}
          imageUrl={avatarUrl || ''}
          username={username}
        />
      )}
    </>
  );
}
